import { useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Asset } from "rujira.js";
import { useTranslation } from "rujira.ui";
import {
  buildRecommendations,
  type TransferSuccessRecommendation,
} from "./transferSuccessRecommendationBuilder";
import { TransferSuccessRecommendationsQuery } from "./__generated__/TransferSuccessRecommendationsQuery.graphql";

const query = graphql`
  query TransferSuccessRecommendationsQuery {
    strategies(first: 100, typenames: ["GhostVault"]) @catch(to: NULL) {
      edges {
        node {
          __typename
          ... on GhostVault {
            asset {
              asset
              chain
              type
              metadata {
                symbol
              }
            }
            status {
              apr {
                value
                status
              }
            }
          }
        }
      }
    }
    finV3 @catch(to: NULL) {
      ranges(first: 100, status: OPEN, sortBy: APR, sortDir: DESC) {
        edges {
          node {
            pair {
              assetBase {
                asset
                chain
                type
                metadata {
                  symbol
                }
              }
              assetQuote {
                asset
                chain
                type
                metadata {
                  symbol
                }
              }
            }
            analytics {
              apr
            }
          }
        }
      }
    }
    finV2(first: 100, sortBy: VOLUME_USD, sortDir: DESC) @catch(to: NULL) {
      edges {
        node {
          assetBase {
            asset
            chain
            type
            metadata {
              symbol
            }
          }
          assetQuote {
            asset
            chain
            type
            metadata {
              symbol
            }
          }
        }
      }
    }
    ghostCredit @catch(to: NULL) {
      collaterals {
        asset {
          asset
          chain
          type
          metadata {
            symbol
          }
        }
        ratio
      }
      vaults {
        borrower {
          asset {
            metadata {
              symbol
            }
          }
          vault {
            status {
              debtRate
            }
          }
        }
      }
    }
    staking @catch(to: NULL) {
      pools {
        bondAsset {
          metadata {
            symbol
          }
        }
        summary {
          apr {
            value
            status
          }
        }
      }
    }
  }
`;

export type { TransferSuccessRecommendation };

type Translate = ReturnType<typeof useTranslation>["t"];

export const useTransferSuccessRecommendations = (
  asset: Asset,
  t: Translate
): TransferSuccessRecommendation[] => {
  const data = useLazyLoadQuery<TransferSuccessRecommendationsQuery>(
    query,
    {},
    { fetchPolicy: "store-or-network" }
  );

  return useMemo(() => buildRecommendations(asset, data, t), [asset, data, t]);
};
